import {ReactNode} from 'react';
import classNames from 'classnames';

export interface SegmentedOption<T extends string | number> {
    value: T;
    label: ReactNode;
    // Required when the label is only an icon.
    ariaLabel?: string;
}

interface SegmentedControlProps<T extends string | number> {
    value: T;
    options: SegmentedOption<T>[];
    onChange: (value: T) => void;
    'aria-label': string;
    size?: 'sm' | 'md';
    className?: string;
}

/*
 * Switches the view of the same content: a neutral track with a raised
 * white chip on the selected option. Selection is never a block of colour.
 */
export default function SegmentedControl<T extends string | number>({
    value,
    options,
    onChange,
    size = 'md',
    className,
    ...rest
}: SegmentedControlProps<T>) {
    return (
        <div
            role="group"
            aria-label={rest['aria-label']}
            className={classNames('mx-segmented', `mx-segmented-${size}`, className)}
        >
            {options.map(option => (
                <button
                    key={String(option.value)}
                    type="button"
                    aria-pressed={option.value === value}
                    aria-label={option.ariaLabel}
                    className={classNames('mx-segmented-option', {
                        active: option.value === value
                    })}
                    onClick={() => onChange(option.value)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
}
